/**
 * Node de mensagem exclusivo para Instagram
 * Suporta três modos: Reply Público, Reply Privado e DM Direto
 * 
 * RELEASE: 1.0.0 - 2025-01-26 - Novo componente para fluxos Instagram
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { Instagram } from 'lucide-react';
import { FlowNodeData, InstagramReplyMode } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/**
 * Labels e ícones para os modos de resposta
 */
/**
 * Labels para os modos de resposta (simplificado)
 */
const replyModeLabels: Record<InstagramReplyMode, string> = {
  public_reply: 'Reply Público',
  private_reply: 'Reply Privado',
  direct_message: 'DM Direto',
};

/**
 * Componente visual do node de mensagem Instagram
 */
export function InstagramMessageNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config || {};
  
  // Callbacks injetados via FlowCanvas
  const onEdit = (nodeData as any).onEdit;
  const onDuplicate = (nodeData as any).onDuplicate;
  const onDelete = (nodeData as any).onDelete;
  const onTestFromHere = (nodeData as any).onTestFromHere;
  
  // Extrai configurações
  const replyMode: InstagramReplyMode = config.reply_mode || 'direct_message';
  const text = config.text || '';
  const hasMedia = config.media_url && config.media_type;
  
  // Label do modo selecionado
  const modeLabel = replyModeLabels[replyMode];
  
  // Preview do texto (mesmo padrão do MessageNode)
  const textPreview = text.length > 50 ? text.substring(0, 50) + '...' : text;

  return (
    <div className="bg-card rounded-lg shadow-lg border-2 border-border min-w-[200px] max-w-[280px]">
      {/* Toolbar flutuante */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />
      
      {/* Handle de entrada - padrão */}
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-border !w-3 !h-3"
      />
      
      {/* Cabeçalho - mesmo padrão do MessageNode */}
      <div className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 p-2 rounded">
            <Instagram className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm text-foreground">
              {nodeData.config?.nickname || 'Mensagem Instagram'}
            </div>
            <div className="text-xs text-muted-foreground">{modeLabel}</div>
          </div>
        </div>
        
        {/* Preview do texto - mesmo padrão do MessageNode */}
        {textPreview && (
          <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2 mt-2">
            {textPreview}
          </div>
        )}
        
        {/* Indicador de mídia (apenas DM Direto) */}
        {hasMedia && replyMode === 'direct_message' && (
          <div className="text-[10px] text-muted-foreground mt-1">
            📎 Mídia anexada
          </div>
        )}
      </div>
      
      {/* Handle de saída - padrão */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-border !w-3 !h-3"
      />
    </div>
  );
}
